import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { NgUnsubscribe } from '@appRoot/core/interfaces';

import { Customer } from '@appRoot/core/customer/models';
import * as customerSelectors from '@appRoot/core/customer/ngrx-store/selectors/customer.selectors';
import { loadCustomerById } from '@appRoot/core/customer/utils';
import { CustomerInfoService } from '@appRoot/shared/components/customer-info/customer-info.service';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { select, Store } from '@ngrx/store';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { BehaviorSubject, race, Subject, timer } from 'rxjs';

import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';


@Component({
    selector: 'app-customer-info-tooltip',
    templateUrl: './customer-info-tooltip.component.html',
    styleUrls: ['./customer-info-tooltip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerInfoTooltipComponent implements OnInit, NgUnsubscribe {

    @ViewChild('pop') tooltip: TooltipDirective;
    @ViewChild('pop', { static: true, read: ElementRef }) tooltipElemRef: ElementRef;

    readonly ngUnsubscribe = new Subject<void>();
    public directiveElem: ElementRef; // From directive must be passed!
    public faSpinner = faSpinner;
    public toggleDisplaySubject: Subject<'show' | 'hide'> = new Subject();
    private isShown: boolean = false;
    private customerId$: BehaviorSubject<number> = new BehaviorSubject(null);
    private toShow: boolean = false;

    constructor(
        private store: Store<any>,
        private cdRef: ChangeDetectorRef,
        private renderer: Renderer2,
        private customerInfoService: CustomerInfoService
    ) { }

    @Input()
    set customerId(customerId: number) {
        this.customerId$.next(customerId);
    }

    private _customer: Customer;

    get customer() {
        return this._customer;
    }

    set customer(val: Customer) {
        this._customer = val;
        this.cdRef.detectChanges();
    }

    @HostListener('mouseenter', ['$event']) onMouseEnter(event) {
        this.show();
    }

    @HostListener('mouseleave', ['$event']) onMouseLeave(event) {
        this.hide();
    }

    ngOnInit(): void {
        this.getCustomer();
        this.toggleDisplayHandler();
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }

    unsubscribe(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    show() {
        this.toggleDisplaySubject.next('show');
    }

    hide() {
        this.toggleDisplaySubject.next('hide');
    }

    show$() {
        return this.toggleDisplaySubject.pipe(filter(e => e === 'show'));
    }

    hide$() {
        return this.toggleDisplaySubject.pipe(filter(e => e === 'hide'));
    }

    hideAllTooltips() {
        this.customerInfoService.toggleAllTooltips$.next('hide');
    }

    showAllTooltips() {
        this.customerInfoService.toggleAllTooltips$.next('show');
    }

    allTooltipsHide$() {
        return this.customerInfoService.toggleAllTooltips$.pipe(filter(e => e === 'hide'));
    }

    allTooltipsShow$() {
        return this.customerInfoService.toggleAllTooltips$.pipe(filter(e => e === 'show'));
    }

    private loadCustomer$() {
        return this.customerId$.pipe(
            filter(e => !!e),
            switchMap(customerId => loadCustomerById(this.store, customerId)),
        );
    }

    private getCustomer() {
        this.customerId$.pipe(
            tap(id => this.customer = null),
            switchMap(customerId => this.store.pipe(select(customerSelectors.getCustomerById(customerId)))),
            takeUntil(this.ngUnsubscribe),
        ).subscribe((customer: Customer) => {
            this.customer = customer;
        });
    }

    private toggleDisplayHandler(): void {
        this.show$().pipe(
            filter(e => !this.isShown),
            takeUntil(this.ngUnsubscribe),
        ).subscribe(e => {
            this.hideAllTooltips();
            this.toShow = true;
            this.showAllTooltips();
        });

        this.allTooltipsShow$().pipe(
            filter(e => this.toShow),
            takeUntil(this.ngUnsubscribe)
        ).subscribe(e => this.showTooltip());

        this.allTooltipsHide$().pipe(
            tap(e => this.toShow = false),
            takeUntil(this.ngUnsubscribe)
        ).subscribe(e => this.hideTooltip());

        this.show$().pipe(
            switchMap(e => this.loadCustomer$()),
            takeUntil(this.ngUnsubscribe),
        ).subscribe();

        this.hide$().pipe(
            tap(e => this.toShow = false),
            switchMap(e => (
                race(
                    timer(200),
                    this.show$()
                )
            )),
            takeUntil(this.ngUnsubscribe),
        ).subscribe(e => {
            if(e !== 'show') {
                this.toShow = false;
                this.hideTooltip();
            }
        });
    }

    private configureCoordinates(): void {
        if(this.directiveElem) {
            const tooltipElem = this.tooltipElemRef.nativeElement;
            const height = this.directiveElem.nativeElement.offsetHeight;
            const width = this.directiveElem.nativeElement.offsetWidth;
            this.renderer.setStyle(tooltipElem, 'width', width + 'px');
            this.renderer.setStyle(tooltipElem, 'height', height + 'px');
            this.renderer.setStyle(tooltipElem, 'marginTop', -height + 'px');
        } else {
            throw new Error("Property 'directiveElem' of 'CustomerInfoTooltipComponent' has not been passed.");
        }
    }

    private showTooltip(): void {
        if(this.isShown) return;
        this.configureCoordinates();
        this.isShown = true;
        this.tooltip.show();
    }

    private hideTooltip(): void {
        this.isShown = false;
        this.tooltip.hide();
    }

}
